import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Stale-while-revalidate for list pages.
 *
 * Some list endpoints take seconds (`GET /kontroledge/ports` does several external calls
 * per port). Coming back to a list you already loaded — through a view, the menu or
 * Back — used to replay that whole wait behind a full-screen overlay. Now the last
 * answer for the same key paints at once and the request refreshes it in the background.
 *
 * The cache lives in memory only: a reload or a logout (Auth0 leaves the page) starts
 * clean, so nothing outlives the session it was fetched in. The key must carry everything
 * the answer depends on — at least the tenant — or one customer's rows would paint under
 * another's lens.
 */
const cache = new Map<string, unknown>();

/** For tests. */
export function clearCachedFetches(): void {
  cache.clear();
}

/**
 * The same cache for pages that assemble their answer in several requests (a detail's
 * core GET plus its extras) and so cannot hand one `load` to the hook: read the last
 * assembled answer to paint at once, write the new one when it is complete.
 */
export function readCachedFetch<T>(key: string | null): T | undefined {
  return key ? (cache.get(key) as T | undefined) : undefined;
}

export function writeCachedFetch<T>(key: string | null, value: T): void {
  if (key) cache.set(key, value);
}

export interface CachedFetch<T> {
  /** The freshest answer for this key: cached, then replaced by the refresh. */
  data: T | undefined;
  /** Set only when there is nothing to show — a failed refresh keeps the cached rows. */
  error: string | null;
  /** No answer for this key yet: the page has nothing to paint. */
  isLoading: boolean;
  /** An answer is shown and a newer one is on its way. */
  isRefreshing: boolean;
  reload: () => void;
}

function messageOf(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Network error";
}

/**
 * `load` throws to report a failure; its message is what the page shows. Pass `key` null
 * to wait (e.g. until the tenant is known).
 */
export function useCachedFetch<T>(key: string | null, load: () => Promise<T>): CachedFetch<T> {
  const [data, setData] = useState<T | undefined>(() => (key ? (cache.get(key) as T | undefined) : undefined));
  const [dataKey, setDataKey] = useState<string | null>(key);
  const [error, setError] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [nonce, setNonce] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;
  const seqRef = useRef(0);

  // The key moved (another customer): show that key's cached answer, or nothing — never
  // the previous key's rows.
  if (dataKey !== key) {
    setDataKey(key);
    setData(key ? (cache.get(key) as T | undefined) : undefined);
    setError(null);
  }

  useEffect(() => {
    if (!key) return undefined;
    const seq = ++seqRef.current;
    let cancelled = false;
    setInFlight(true);

    loadRef
      .current()
      .then((value) => {
        cache.set(key, value);
        if (cancelled || seq !== seqRef.current) return;
        setData(value);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (cancelled || seq !== seqRef.current) return;
        // Rows already on screen stay there; the next visit retries.
        if (!cache.has(key)) setError(messageOf(failure));
      })
      .finally(() => {
        if (!cancelled && seq === seqRef.current) setInFlight(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const hasAnswer = data !== undefined || error !== null;
  return {
    data,
    error,
    isLoading: key !== null && !hasAnswer,
    isRefreshing: inFlight && data !== undefined,
    reload,
  };
}
